import {
  solitaire,
  SUIT_SYMBOLS,
  type Card,
  type SolitaireMoveDest,
  type SolitaireMoveSource,
  type SolitaireView,
} from '@cardengine/shared';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useEffect, useRef, useState } from 'react';
import { useStore } from '../store.js';
import { PlayingCard } from './PlayingCard.js';

/** Two sources are the same pick-up if they name the same cards. */
function sameSource(a: SolitaireMoveSource, b: SolitaireMoveSource): boolean {
  if (a.zone !== b.zone) return false;
  if (a.zone === 'tableau' && b.zone === 'tableau') return a.index === b.index && a.depth === b.depth;
  if (a.zone === 'foundation' && b.zone === 'foundation') return a.suit === b.suit;
  return true;
}

function sameDest(a: SolitaireMoveDest, b: SolitaireMoveDest): boolean {
  if (a.zone !== b.zone) return false;
  if (a.zone === 'tableau' && b.zone === 'tableau') return a.index === b.index;
  if (a.zone === 'foundation' && b.zone === 'foundation') return a.suit === b.suit;
  return false;
}

/**
 * Where a card sits down its column.
 *
 * The two spacings have to be composed rather than applied to a shared row
 * number: face-down cards are stacked tightly and face-up ones spread out, so
 * multiplying a single index by one step or the other puts the face-up run in
 * the wrong place entirely.
 */
function offsetFor(downCount: number, upIndex: number | null): string {
  if (upIndex === null) return `calc(${downCount} * var(--stack-step-down))`;
  return `calc(${downCount} * var(--stack-step-down) + ${upIndex} * var(--stack-step))`;
}

/**
 * What the player has hold of.
 *
 * Anything can be picked up, including a card with nowhere to go and a
 * face-down one — being told why a move is refused is far more use than a card
 * that will not budge. `liftable` records whether the engine would actually
 * allow it, which is what decides between a move and a bounce.
 */
type Grip =
  | { kind: 'cards'; from: SolitaireMoveSource; cards: Card[]; liftable: boolean; key: string }
  | { kind: 'faceDown'; key: string };

/** Matches the CSS bounce. */
const BOUNCE_MS = 420;

export function SolitaireBoard() {
  const view = useStore((s) => s.solitaire);
  const move = useStore((s) => s.solitaireMove);
  const draw = useStore((s) => s.solitaireDraw);
  const autoComplete = useStore((s) => s.solitaireAutoComplete);
  const pushToast = useStore((s) => s.pushToast);
  const highlightPlayable = useStore((s) => s.highlightPlayable);
  const setHighlightPlayable = useStore((s) => s.setHighlightPlayable);

  const [grip, setGrip] = useState<Grip | null>(null);
  const [bouncing, setBouncing] = useState<string | null>(null);
  const bounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The same sensors as the Switch table, so a tap still reads as a click and
  // a drag needs a deliberate pull.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const moveNumber = view?.moveNumber ?? 0;
  useEffect(() => setGrip(null), [moveNumber]);

  useEffect(
    () => () => {
      if (bounceTimer.current) clearTimeout(bounceTimer.current);
    },
    [],
  );

  if (!view) return <p className="game__loading">Dealing…</p>;

  const moves = view.legalMoves;
  const canLift = (from: SolitaireMoveSource) =>
    moves.some((m) => m.kind === 'move' && sameSource(m.from, from));

  /** Only a genuinely liftable grip lights anything up. */
  const heldSource = grip?.kind === 'cards' && grip.liftable ? grip.from : null;
  const canDrop = (to: SolitaireMoveDest) =>
    heldSource !== null &&
    moves.some((m) => m.kind === 'move' && sameSource(m.from, heldSource) && sameDest(m.to, to));

  const liftableCount = new Set(
    moves.filter((m) => m.kind === 'move').map((m) => JSON.stringify(m.from)),
  ).size;

  function refuse(key: string, message: string): void {
    pushToast(message, 'warn');
    if (bounceTimer.current) clearTimeout(bounceTimer.current);
    setBouncing(key);
    bounceTimer.current = setTimeout(() => setBouncing(null), BOUNCE_MS);
    setGrip(null);
  }

  /** Settle a grip against a destination: move it, or bounce and say why. */
  function resolve(held: Grip, to: SolitaireMoveDest): void {
    if (held.kind === 'faceDown') {
      refuse(held.key, 'That card is face down — clear the ones on top of it first.');
      return;
    }
    if (!held.liftable) {
      refuse(held.key, solitaire.describeUnliftable(held.cards));
      return;
    }
    const legal = moves.some(
      (m) => m.kind === 'move' && sameSource(m.from, held.from) && sameDest(m.to, to),
    );
    if (legal) {
      move(held.from, to);
      return;
    }
    refuse(held.key, solitaire.describeRefusal(view as SolitaireView, held.cards, to));
  }

  /** Click to lift; click again to bank it or put it down. */
  function tap(next: Grip): void {
    if (next.kind === 'faceDown') {
      refuse(next.key, 'That card is face down — clear the ones on top of it first.');
      return;
    }
    if (grip?.kind === 'cards' && sameSource(grip.from, next.from) && grip.liftable) {
      const home = moves.find(
        (m) => m.kind === 'move' && sameSource(m.from, next.from) && m.to.zone === 'foundation',
      );
      if (home && home.kind === 'move') move(next.from, home.to);
      else setGrip(null);
      return;
    }
    if (!next.liftable) {
      refuse(next.key, solitaire.describeUnliftable(next.cards));
      return;
    }
    setGrip(next);
  }

  function handleDragStart(event: DragStartEvent): void {
    const held = event.active.data.current?.grip as Grip | undefined;
    if (held) setGrip(held);
  }

  function handleDragEnd(event: DragEndEvent): void {
    const held = event.active.data.current?.grip as Grip | undefined;
    const to = event.over?.data.current?.to as SolitaireMoveDest | undefined;
    setGrip(null);
    // Dropped on open felt: no move, and nothing worth complaining about.
    if (held && to) resolve(held, to);
  }

  const stockLabel = view.stockCount > 0 ? `Turn (${view.stockCount})` : 'Turn the pile back over';
  const stockDead = view.stockCount === 0 && !view.canRecycle;
  const bounceClass = (key: string) => (bouncing === key ? 'card--bounce' : '');

  const dragging = grip?.kind === 'cards' ? grip.cards : null;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setGrip(null)}
    >
      <div className="solitaire" aria-label="Solitaire table">
        <div className="solitaire__top">
          <div className="solitaire__stockwaste">
            <button
              type="button"
              className={`solitaire__stock ${liftableCount === 0 && !stockDead ? 'is-urged' : ''}`}
              onClick={draw}
              disabled={stockDead}
              aria-label={stockLabel}
              title={stockLabel}
            >
              {view.stockCount > 0 ? (
                <PlayingCard card={{ id: 'stock', suit: 'S', rank: 'A' }} faceDown />
              ) : (
                <span className={`solitaire__slot ${view.canRecycle ? 'solitaire__slot--active' : ''}`}>
                  {view.canRecycle ? '↻' : '—'}
                </span>
              )}
              <span className="solitaire__count">{view.stockCount}</span>
            </button>

            <div className="solitaire__waste" aria-label="Turned-up cards">
              {view.waste.length === 0 ? (
                <span className="solitaire__slot" />
              ) : (
                view.waste.map((card, i) => {
                  const isTop = i === view.waste.length - 1;
                  const from: SolitaireMoveSource = { zone: 'waste' };
                  const movable = canLift(from);
                  return (
                    <DraggableCard
                      key={card.id}
                      id={card.id}
                      grip={{ kind: 'cards', from, cards: [card], liftable: movable, key: 'waste' }}
                      disabled={!isTop}
                      className="solitaire__wastecard"
                      style={{ left: `calc(${i} * var(--waste-fan))` }}
                      label={`${card.rank}${SUIT_SYMBOLS[card.suit]}`}
                      onActivate={tap}
                    >
                      <PlayingCard
                        card={card}
                        className={isTop ? bounceClass('waste') : ''}
                        playable={isTop && highlightPlayable && movable}
                        dimmed={isTop && highlightPlayable && !movable}
                        selected={isTop && heldSource?.zone === 'waste'}
                      />
                    </DraggableCard>
                  );
                })
              )}
            </div>
          </div>

          <div className="solitaire__foundations" aria-label="Foundations">
            {view.foundations.map((foundation) => (
              <FoundationSlot
                key={foundation.suit}
                foundation={foundation}
                held={heldSource}
                showTarget={highlightPlayable && canDrop({ zone: 'foundation', suit: foundation.suit })}
                bounceClass={bounceClass}
                onActivate={(next) => (grip ? resolve(grip, { zone: 'foundation', suit: foundation.suit }) : tap(next))}
              />
            ))}
          </div>
        </div>

        <div className="solitaire__tableau" aria-label="Tableau">
          {view.tableau.map((column, index) => (
            <Column
              key={index}
              index={index}
              column={column}
              held={heldSource}
              grip={grip}
              highlight={highlightPlayable}
              showTarget={highlightPlayable && canDrop({ zone: 'tableau', index })}
              canLift={canLift}
              bounceClass={bounceClass}
              onTap={tap}
              onDropInto={(to) => grip && resolve(grip, to)}
            />
          ))}
        </div>

        <div className="solitaire__status">
          {view.canAutoComplete && (
            <button type="button" className="btn btn--primary" onClick={autoComplete}>
              Finish it
            </button>
          )}
          <span className="solitaire__hint">
            {view.canAutoComplete
              ? 'Nothing is face down — the rest plays itself.'
              : liftableCount === 0
                ? stockDead
                  ? 'Nothing will move and the stock is spent.'
                  : 'Nothing on the board will move — turn the stock.'
                : highlightPlayable
                  ? `${liftableCount} card${liftableCount === 1 ? '' : 's'} can move.`
                  : 'Drag a card, or click to lift it.'}
          </span>
          <label className="assist">
            <input
              type="checkbox"
              checked={highlightPlayable}
              onChange={(event) => setHighlightPlayable(event.target.checked)}
            />
            <span>Highlight playable cards</span>
          </label>
        </div>
      </div>

      {/*
        A full-size overlay follows the cursor, exactly as the Switch table
        does. Without it the browser draws its own shrunken, washed-out ghost.
      */}
      <DragOverlay dropAnimation={null}>
        {dragging ? (
          <div className="drag-overlay drag-overlay--solitaire">
            {dragging.map((card, i) => (
              <PlayingCard
                key={card.id}
                card={card}
                style={{ marginTop: i === 0 ? 0 : 'calc(var(--card-w) * -1.09)' }}
              />
            ))}
          </div>
        ) : grip?.kind === 'faceDown' ? (
          <div className="drag-overlay drag-overlay--solitaire">
            <PlayingCard card={{ id: 'drag-down', suit: 'S', rank: 'A' }} faceDown />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

interface DraggableCardProps {
  id: string;
  grip: Grip;
  disabled?: boolean;
  className: string;
  style?: React.CSSProperties;
  label: string;
  onActivate: (grip: Grip) => void;
  children: React.ReactNode;
}

/**
 * One liftable thing on the table.
 *
 * No transform is applied to the source — the DragOverlay draws the card that
 * follows the cursor, so moving this one as well would drag a second ghost
 * along with it.
 */
function DraggableCard({
  id,
  grip,
  disabled = false,
  className,
  style,
  label,
  onActivate,
  children,
}: DraggableCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    disabled,
    data: { grip },
  });

  return (
    <button
      ref={setNodeRef}
      type="button"
      className={`${className} ${isDragging ? 'is-dragging' : ''}`}
      style={style}
      disabled={disabled}
      {...listeners}
      {...attributes}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        if (!disabled) onActivate(grip);
      }}
    >
      {children}
    </button>
  );
}

interface FoundationSlotProps {
  foundation: SolitaireView['foundations'][number];
  held: SolitaireMoveSource | null;
  showTarget: boolean;
  bounceClass: (key: string) => string;
  onActivate: (grip: Grip) => void;
}

function FoundationSlot({ foundation, held, showTarget, bounceClass, onActivate }: FoundationSlotProps) {
  const to: SolitaireMoveDest = { zone: 'foundation', suit: foundation.suit };
  const { setNodeRef } = useDroppable({ id: `drop-foundation-${foundation.suit}`, data: { to } });

  const top = foundation.top;
  const key = `foundation:${foundation.suit}`;
  const from: SolitaireMoveSource = { zone: 'foundation', suit: foundation.suit };

  return (
    <div ref={setNodeRef} className={`solitaire__foundation ${showTarget ? 'is-target' : ''}`}>
      {top ? (
        <DraggableCard
          id={top.id}
          grip={{ kind: 'cards', from, cards: [top], liftable: true, key }}
          className="solitaire__foundationcard"
          label={`${top.rank}${SUIT_SYMBOLS[top.suit]} on the ${foundation.suit} foundation`}
          onActivate={onActivate}
        >
          <PlayingCard
            card={top}
            className={bounceClass(key)}
            selected={held?.zone === 'foundation' && held.suit === foundation.suit}
          />
        </DraggableCard>
      ) : (
        <span className="solitaire__slot solitaire__slot--suit">{SUIT_SYMBOLS[foundation.suit]}</span>
      )}
    </div>
  );
}

interface ColumnProps {
  index: number;
  column: SolitaireView['tableau'][number];
  held: SolitaireMoveSource | null;
  grip: Grip | null;
  highlight: boolean;
  showTarget: boolean;
  canLift: (from: SolitaireMoveSource) => boolean;
  bounceClass: (key: string) => string;
  onTap: (grip: Grip) => void;
  onDropInto: (to: SolitaireMoveDest) => void;
}

function Column({
  index,
  column,
  held,
  grip,
  highlight,
  showTarget,
  canLift,
  bounceClass,
  onTap,
  onDropInto,
}: ColumnProps) {
  const to: SolitaireMoveDest = { zone: 'tableau', index };
  const { setNodeRef } = useDroppable({ id: `drop-tableau-${index}`, data: { to } });

  const bare = column.downCount === 0 && column.up.length === 0;

  return (
    <div
      ref={setNodeRef}
      className="solitaire__column"
      onClick={() => grip && onDropInto(to)}
      aria-label={`Column ${index + 1}`}
    >
      {bare && <span className="solitaire__slot solitaire__slot--column" />}

      {/*
        A card-sized outline at the exact spot the run will land, rather than a
        border around the whole column — a column is tall and mostly empty, so
        outlining it drew a huge box around nothing.
      */}
      {showTarget && (
        <span
          className="solitaire__landing"
          style={{ top: offsetFor(column.downCount, column.up.length) }}
          aria-hidden="true"
        />
      )}

      {Array.from({ length: column.downCount }, (_unused, i) => {
        const key = `down:${index}:${i}`;
        return (
          <DraggableCard
            key={`down-${i}`}
            id={`down-${index}-${i}`}
            grip={{ kind: 'faceDown', key }}
            className="solitaire__stacked"
            style={{ top: offsetFor(i, null) }}
            label={`Face-down card in column ${index + 1}`}
            onActivate={onTap}
          >
            <PlayingCard
              card={{ id: `down-${index}-${i}`, suit: 'S', rank: 'A' }}
              faceDown
              className={bounceClass(key)}
            />
          </DraggableCard>
        );
      })}

      {column.up.map((card, i) => {
        // Depth counts from the exposed bottom of the run upwards.
        const depth = column.up.length - i;
        const from: SolitaireMoveSource = { zone: 'tableau', index, depth };
        const run = column.up.slice(column.up.length - depth);
        const key = `tableau:${index}:${depth}`;
        const movable = canLift(from);
        const isHeld = held?.zone === 'tableau' && held.index === index && held.depth >= depth;

        return (
          <DraggableCard
            key={card.id}
            id={card.id}
            grip={{ kind: 'cards', from, cards: run, liftable: movable, key }}
            className="solitaire__stacked solitaire__stacked--up"
            style={{ top: offsetFor(column.downCount, i) }}
            label={`${card.rank}${SUIT_SYMBOLS[card.suit]} in column ${index + 1}`}
            onActivate={(next) => (grip ? onDropInto(to) : onTap(next))}
          >
            <PlayingCard
              card={card}
              className={bounceClass(key)}
              playable={highlight && movable}
              dimmed={highlight && !movable}
              selected={isHeld}
            />
          </DraggableCard>
        );
      })}
    </div>
  );
}

/** Exported for the header readout. */
export function describeStock(stockCount: number, redealsRemaining: number | null): string {
  if (redealsRemaining === null) return `${stockCount} in the stock`;
  return `${stockCount} in the stock · ${redealsRemaining} pass${redealsRemaining === 1 ? '' : 'es'} left`;
}
